import React, {useEffect, useState} from "react";
import Modal from "../../../components/My/Modal";
import {TurnstileButton} from "../components/Button";
import {COMMAND_FORM, FIELD_TYPE, buildDefaultValues, isFormValid, buildPayload, visibleFields} from "./commandForms";
import {commandLabel} from "./mapConstants";

const labelStyle = {display: "block", fontSize: "var(--enigoo-fs-sm)", fontWeight: 600, color: "#475569", marginBottom: "4px"};
const inputStyle = {width: "100%", padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "var(--enigoo-fs-md)", background: "#fff", color: "#1e293b", outline: "none", boxSizing: "border-box"};
const fieldWrapStyle = {marginBottom: "14px"};

const renderField = (field, values, setValue) => {
    const value = values[field.name];
    if (field.type === FIELD_TYPE.CHECKBOX) {
        return (
            <label style={{display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "var(--enigoo-fs-md)", color: "#374151"}}>
                <input type="checkbox" checked={!!value} onChange={(e) => setValue(field.name, e.target.checked)}/>
                {field.label}
            </label>
        );
    }
    if (field.type === FIELD_TYPE.SELECT || field.type === FIELD_TYPE.BOOL_SELECT) {
        return (
            <select value={value} onChange={(e) => setValue(field.name, e.target.value)} style={inputStyle}>
                {field.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        );
    }
    if (field.type === FIELD_TYPE.NUMBER) {
        return <input type="number" value={value} placeholder={field.placeholder}
            onChange={(e) => setValue(field.name, e.target.value)} style={inputStyle}/>;
    }
    if (field.type === FIELD_TYPE.DATETIME) {
        return <input type="datetime-local" value={value}
            onChange={(e) => setValue(field.name, e.target.value)} style={inputStyle}/>;
    }
    return <input type="text" value={value} placeholder={field.placeholder}
        onChange={(e) => setValue(field.name, e.target.value)} style={inputStyle}/>;
};

/**
 * Modal pro odeslání příkazu turniketu s payloadem. `command` je kód (např. COMMAND.OPEN);
 * `null` = modal zavřený. Pole i chování (potvrzovací text, nebezpečný styl, pevný payload)
 * bere z `COMMAND_FORM[command]` (viz commandForms.js).
 *
 * `sendCommand(command, payload, onDone)` odešle příkaz — `onDone(success)` řídí spinner
 * a zavření modalu; Toast a refresh historie řeší volající (viz MapView.onAction).
 */
const CommandFormModal = ({command, turnstile, onClose, sendCommand}) => {
    const config = command ? COMMAND_FORM[command] : null;
    const fields = config?.fields || [];

    const [values, setValues] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setValues(buildDefaultValues(fields));
        setSubmitting(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [command]);

    if (!command || !config) return null;

    const setValue = (name, value) => setValues((prev) => ({...prev, [name]: value}));

    const submit = () => {
        if (!isFormValid(config, values)) return;
        setSubmitting(true);
        sendCommand(command, buildPayload(config, values), (success) => {
            setSubmitting(false);
            if (success) onClose();
        });
    };

    const title = commandLabel(command) + (turnstile ? " — " + (turnstile.description || ("Turniket #" + turnstile.id)) : "");

    return (
        <Modal
            visible={true}
            close={onClose}
            title={title}
            body={
                <div>
                    {config.confirmText && (
                        <p style={{
                            margin: "0 0 14px", fontSize: "var(--enigoo-fs-md)",
                            color: config.dangerous ? "#dc2626" : "#475569",
                            fontWeight: config.dangerous ? 600 : 400,
                        }}>
                            {config.confirmText}
                        </p>
                    )}
                    {visibleFields(fields, values).map((f) => (
                        <div key={f.name} style={fieldWrapStyle}>
                            <label style={labelStyle}>{f.label}{f.required ? " *" : ""}</label>
                            {renderField(f, values, setValue)}
                        </div>
                    ))}
                    {config.note && (
                        <p style={{margin: "8px 0 0", fontSize: "var(--enigoo-fs-sm)", color: "#94a3b8"}}>
                            {config.note}
                        </p>
                    )}
                </div>
            }
            footer={
                <>
                    <TurnstileButton
                        text={submitting ? "Odesílám..." : (config.submitLabel || "Odeslat")}
                        color={config.dangerous ? "danger" : "primary"}
                        disabled={submitting || !isFormValid(config, values)}
                        onClick={submit}
                    />
                    <TurnstileButton text="Zrušit" color="outline" onClick={onClose} disabled={submitting}/>
                </>
            }
        />
    );
};

export default CommandFormModal;
